import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { Download, Loader2 } from "lucide-react";

export default function LeadCaptureModal({ open, onOpenChange, guide }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organisation: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!guide) return;
    setLoading(true);
    try {
      const res = await createLead({ ...form, guide_slug: guide.slug });
      toast.success("Check your inbox your guide is on the way!", {
        description: res.email_sent ? "We've also emailed you the link." : "Email delivery is in progress.",
      });
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", organisation: "" });
    } catch (err) {
      toast.error("Couldn't submit your request", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-modal" className="max-w-lg bg-canvas border-hairline rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">
            {guide ? `Download: ${guide.title}` : "Download guide"}
          </DialogTitle>
          <DialogDescription className="text-muted2">
            Enter your details and we'll email you the guide instantly. No spam promise.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-name" className="text-xs text-muted2">Full name *</Label>
              <Input
                id="lead-name"
                data-testid="lead-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lead-email" className="text-xs text-muted2">Email *</Label>
              <Input
                id="lead-email"
                type="email"
                data-testid="lead-email-input"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-phone" className="text-xs text-muted2">Phone</Label>
              <Input
                id="lead-phone"
                data-testid="lead-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="lead-org" className="text-xs text-muted2">Organisation</Label>
              <Input
                id="lead-org"
                data-testid="lead-org-input"
                value={form.organisation}
                onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="lead-submit"
            disabled={loading}
            className="btn-ochre w-full justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {loading ? "Sending…" : "Email me the guide"}
          </button>
          <p className="text-[11px] text-muted2 leading-relaxed">
            By submitting, you agree to receive related emails. See our <a className="underline" href="/privacy">privacy policy</a>.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
